import { useNavigate } from "react-router-dom";
import { Flame, Phone, ChatCircleDots, ArrowRight, Warning, Moon } from "@phosphor-icons/react";
import { formatEUR } from "@/lib/api";
import { track, USAGE_EVENTS } from "@/lib/usage";
import EmailRelanceButton from "./EmailRelanceButton";

/**
 * V4.25 — "Mandats à sécuriser" + "Dormants à réactiver".
 *
 * Bloc inséré dans Commando entre Priorité unique et Relances critiques.
 *
 * Doctrine : quels sont les 5 dossiers qui ont le plus de chances de
 * signer un mandat si j'agis aujourd'hui ?
 * - mandats_a_securiser : top 5 score >= 20, mandat non signé, étape ∈ {prospect, compromis}
 * - dormants_a_reactiver : Safe=Silence (rien si 0 dossier dormant pertinent)
 */

const CS_LABELS = {
  a_appeler: "À appeler",
  rdv_a_faire: "RDV à faire",
  rdv_fait: "RDV fait",
};

const PM_LABELS = {
  en_reflexion: "En réflexion",
  en_recherche: "En recherche",
  offre_en_cours: "Offre en cours",
};

function scoreColor(score) {
  if (score >= 80) return { bg: "var(--cf-danger-soft)", fg: "var(--cf-danger-text)", label: "très chaud" };
  if (score >= 60) return { bg: "var(--cf-warning-soft)", fg: "var(--cf-warning-text)", label: "chaud" };
  if (score >= 40) return { bg: "var(--cf-warning-soft)", fg: "var(--cf-warning-text)", label: "à travailler" };
  return { bg: "var(--cf-bg-2)", fg: "var(--cf-neutral-text)", label: "faible" };
}

function digitsOnly(phone) {
  if (!phone) return "";
  return String(phone).replace(/[^\d+]/g, "");
}

function MandatItem({ m }) {
  const navigate = useNavigate();
  const c = scoreColor(m.score);
  const phone = digitsOnly(m.client_phone);
  const hasPhone = phone.length >= 8;
  const waPhone = phone.replace(/^\+/, "").replace(/^0/, "33"); // FR default

  const openFiche = () => navigate(`/dossier/${m.id}`);
  const callPhone = (e) => {
    e.stopPropagation();
    if (hasPhone) {
      track(USAGE_EVENTS.COMMANDO_APPEL, { dossier_id: m.id, etape: m.etape });
      window.location.href = `tel:${phone}`;
    }
  };
  const openWA = (e) => {
    e.stopPropagation();
    if (hasPhone) {
      track(USAGE_EVENTS.COMMANDO_WHATSAPP, { dossier_id: m.id, etape: m.etape });
      window.open(`https://wa.me/${waPhone}`, "_blank", "noopener");
    }
  };

  return (
    <li
      onClick={openFiche}
      className="rounded-xl p-3 border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
      data-testid={`mandat-securiser-${m.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Pastille score */}
        <div
          className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
          style={{ background: c.bg, color: c.fg }}
          title={c.label}
          data-testid={`mandat-score-${m.id}`}
        >
          <span className="font-display text-[17px] font-extrabold tabular-nums leading-none">{m.score}</span>
          <span className="text-[8.5px] font-bold uppercase tracking-wide opacity-80 mt-0.5">score</span>
        </div>

        {/* Centre : nom + meta + raisons */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-[13.5px] text-slate-900 truncate">
              {m.nom || m.client_name || "(Sans nom)"}
            </div>
            {m.ca_potentiel > 0 && (
              <span className="text-[11px] font-extrabold tabular-nums text-slate-700">
                {formatEUR(m.ca_potentiel)}
              </span>
            )}
          </div>

          {/* Meta : maturité · contact · dernier contact */}
          <div className="text-[10.5px] font-semibold text-slate-500 mt-0.5 leading-tight flex flex-wrap gap-x-2">
            {m.project_maturity && <span>{PM_LABELS[m.project_maturity] || m.project_maturity}</span>}
            {m.contact_status && (
              <span className="text-slate-400">
                · {CS_LABELS[m.contact_status] || m.contact_status}
              </span>
            )}
            {m.days_since_contact != null && (
              <span className="text-slate-400">
                · contact il y a {m.days_since_contact}j
              </span>
            )}
          </div>

          {/* Top 3 raisons */}
          {m.reasons?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {m.reasons.map((r, idx) => (
                <span
                  key={idx}
                  className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
                  data-testid={`mandat-reason-${m.id}-${idx}`}
                >
                  {r.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* CTAs droite */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {hasPhone ? (
            <>
              <button
                type="button"
                onClick={callPhone}
                className="w-8 h-8 rounded-lg bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] hover:brightness-95 flex items-center justify-center transition-colors"
                title={`Appeler ${phone}`}
                data-testid={`mandat-call-${m.id}`}
              >
                <Phone size={13} weight="fill" />
              </button>
              <button
                type="button"
                onClick={openWA}
                className="w-8 h-8 rounded-lg bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] hover:brightness-95 flex items-center justify-center transition-colors"
                title="WhatsApp"
                data-testid={`mandat-wa-${m.id}`}
              >
                <ChatCircleDots size={13} weight="fill" />
              </button>
            </>
          ) : (
            <div
              className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"
              title="Téléphone manquant — Appeler/WhatsApp désactivés"
              data-testid={`mandat-nophone-${m.id}`}
            >
              <Warning size={13} weight="fill" />
            </div>
          )}
          {/* V4.40 — Bouton Email contextuel pour mandat à sécuriser (audience client) */}
          <div onClick={(e) => e.stopPropagation()}>
            <EmailRelanceButton
              audience="client"
              dossier={m}
              compact
              testid={`mandat-email-${m.id}`}
            />
          </div>
          <ArrowRight size={14} className="text-slate-300" />
        </div>
      </div>
    </li>
  );
}

function DormantItem({ d }) {
  const navigate = useNavigate();
  return (
    <li
      onClick={() => navigate(`/dossier/${d.id}`)}
      className="rounded-xl p-2.5 border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-3"
      data-testid={`dormant-${d.id}`}
    >
      <Moon size={16} weight="fill" className="text-slate-300 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[12.5px] text-slate-700 truncate">
          {d.nom || d.client_name}
        </div>
        <div className="text-[10.5px] text-slate-500 font-semibold leading-tight">
          {d.reason}
          {d.ca_potentiel > 0 && (
            <span className="text-slate-400"> · {formatEUR(d.ca_potentiel)}</span>
          )}
        </div>
      </div>
      <ArrowRight size={12} className="text-slate-300 flex-shrink-0" />
    </li>
  );
}

export default function MandatsASecuriser({ mandats = [], dormants = [] }) {
  if (!mandats.length && !dormants.length) return null;

  return (
    <>
      {mandats.length > 0 && (
        <section className="card-base p-4 mb-4" data-testid="commando-mandats-securiser">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} weight="fill" color="var(--cf-danger)" />
            <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
              Mandats à sécuriser
            </h2>
            <span className="ml-auto px-2 py-0.5 rounded-full bg-[color:var(--cf-danger-soft)] text-[color:var(--cf-danger-text)] text-[11px] font-extrabold tabular-nums">
              {mandats.length}
            </span>
          </div>
          <ul className="space-y-2">
            {mandats.map((m) => (
              <MandatItem key={m.id} m={m} />
            ))}
          </ul>
          <p className="mt-3 text-[10.5px] text-slate-400 font-semibold leading-snug">
            Top 5 des dossiers les plus proches d'une signature de mandat (maturité, contact, fenêtre temporelle, montant).
          </p>
        </section>
      )}

      {dormants.length > 0 && (
        <section className="card-base p-3.5 mb-4" data-testid="commando-dormants">
          <div className="flex items-center gap-2 mb-2.5">
            <Moon size={14} weight="fill" color="var(--cf-text-3)" />
            <h3 className="font-display text-[13.5px] md:text-sm font-extrabold text-slate-700">
              Dormants à réactiver
            </h3>
            <span className="ml-auto px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10.5px] font-extrabold tabular-nums">
              {dormants.length}
            </span>
          </div>
          <ul className="space-y-1.5">
            {dormants.map((d) => (
              <DormantItem key={d.id} d={d} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
